/**
 * The enquiry book's wire shapes and the small readings every screen of it
 * shares (13 Oct 2026): whether this is the owner (who alone reads the whole
 * book), what a follow-up date says,
 * and the two ways to reach the person — a call and a WhatsApp.
 */
import { useQuery } from '@tanstack/react-query';
import {
  isAsmRole,
  keepsEnquiries,
  phoneKey,
  readsEveryEnquiry,
  type EnquiryChannelKey,
  type EnquiryOutcomeKey,
  type EnquirySourceKey,
  type LeadCallResultKey,
  type DealerOnboardingStatus,
} from '@bright/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { MeetingRow } from '@/features/field/field';

export interface EnquiryRow {
  id: string;
  customerName: string;
  phone: string | null;
  area: string | null;
  wanted: string | null;
  source: EnquirySourceKey;
  channel: EnquiryChannelKey | null;
  note: string | null;
  followUpAt: string | null;
  outcome: EnquiryOutcomeKey;
  outcomeNote: string | null;
  settledAt: string | null;
  createdAt: string;
  lastUpdateAt: string | null;
  takenBy: string | null;
  takenById: string | null;
  counter: string | null;
  updates: number;
  /** Meetings logged against it, and the last one's time (27 Oct 2026: one desk for both). */
  meetings: number;
  lastMetAt: string | null;
  /** Handed over from the telecalling team's lead sheets (19 Oct 2026). */
  fromLead: { caller: string | null; calls: number; made: boolean } | null;
}

export interface PhoneMatch {
  id: string;
  customerName: string;
  createdAt: string;
  takenBy: string | null;
  outcome: EnquiryOutcomeKey;
}

export interface EnquiryDetail extends EnquiryRow {
  history: Array<{ id: string; at: string; by: string | null; note: string | null; outcome: EnquiryOutcomeKey | null; followUpAt: string | null }>;
  samePhone: PhoneMatch[];
  canUpdate: boolean;
  /** Being made a mitr or a dealer from it (29 Oct 2026), when it is. */
  onboarding?: { id: string; kind: 'DEALER' | 'MITRA'; status: DealerOnboardingStatus } | null;
  /** The telecalling team's calls before it was handed over, oldest first. */
  leadCalls: Array<{ at: string; by: string | null; result: LeadCallResultKey | null; note: string | null }>;
  /** Its meetings, oldest first, with their proof. */
  meetingLog: MeetingRow[];
}

export type EnquiryTab = 'due' | 'open' | 'won' | 'lost' | 'all';

export interface EnquiryPage {
  data: EnquiryRow[];
  meta: { page: number; pageSize: number; total: number; totalPages: number; open: number; due: number; won: number; lost: number; all: number };
}

export interface EnquiryTeam {
  people: Array<{
    userId: string | null;
    name: string;
    role: string | null;
    all: number;
    open: number;
    won: number;
    lost: number;
    due: number;
    conversion: number | null;
    thisMonth: { all: number; won: number };
  }>;
  bySource: Array<{ source: EnquirySourceKey; all: number; won: number }>;
  byChannel: Array<{ channel: EnquiryChannelKey | null; all: number; won: number }>;
  totals: { all: number; open: number; won: number; lost: number; due: number; conversion: number | null };
  month: string;
}

export const enquiryKeys = {
  all: ['enquiries'] as const,
  list: (filters: Record<string, string | number | undefined>) => ['enquiries', 'list', filters] as const,
  detail: (id: string) => ['enquiries', 'detail', id] as const,
  team: ['enquiries', 'team'] as const,
  summary: ['enquiries', 'summary'] as const,
  lookup: (key: string) => ['enquiries', 'lookup', key] as const,
};

/** How this sign-in keeps the book: whose part it reads, and what its form must ask. */
export function useEnquiryRole() {
  // The session keeps the permissions beside the user, not on it: ask `has`.
  const { user, has } = useAuth();
  const roleKey = user?.role.key ?? null;
  const asm = isAsmRole(roleKey);
  const counter = Boolean(user?.scopeParty);
  return {
    keeps: Boolean(user) && keepsEnquiries({ roleKey, permissions: has('vehicles:sell') ? ['vehicles:sell'] : [] }),
    asm,
    /** The store's own counter: its enquiries carry the counter, so "what for" is optional. */
    counter,
    /** The owner's desks read every enquiry, and the team view (13 Oct 2026); everyone else their own. */
    wholeBook: readsEveryEnquiry(user),
  };
}

/** The reminder's figures: follow-ups due by tonight. */
export function useEnquirySummary(enabled: boolean) {
  return useQuery({
    queryKey: enquiryKeys.summary,
    queryFn: () => api.get<{ due: number; open: number }>('/enquiries/summary'),
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Today in India, "YYYY-MM-DD", and a day so many days on. */
export const istDayKey = (offsetDays = 0) => new Date(Date.now() + 330 * 60_000 + offsetDays * 86_400_000).toISOString().slice(0, 10);

/** The IST day an instant falls on. */
const istKeyOf = (iso: string) => new Date(new Date(iso).getTime() + 330 * 60_000).toISOString().slice(0, 10);

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const dayLabel = (key: string) => `${Number(key.slice(8, 10))} ${SHORT_MONTHS[Number(key.slice(5, 7)) - 1]}`;

/** What an open enquiry's next call says: overdue, today, or the day. Null when there is none. */
export function followUpState(row: { outcome: EnquiryOutcomeKey; followUpAt: string | null }): { text: string; tone: 'signal' | 'warning' | 'neutral' } | null {
  if (row.outcome !== 'OPEN' || !row.followUpAt) return null;
  const key = istKeyOf(row.followUpAt);
  const today = istDayKey();
  if (key < today) {
    const days = Math.round((Date.parse(today) - Date.parse(key)) / 86_400_000);
    return { text: `Overdue ${days} d`, tone: 'signal' };
  }
  if (key === today) return { text: 'Call back today', tone: 'warning' };
  return { text: `Call ${dayLabel(key)}`, tone: 'neutral' };
}

/** "tel:" and WhatsApp links for a number the book can read; null otherwise. */
export function reachOf(phone: string | null): { call: string; whatsapp: string } | null {
  const key = phoneKey(phone);
  if (!key) return null;
  return { call: `tel:+91${key}`, whatsapp: `https://wa.me/91${key}` };
}

export const OUTCOME_TONE: Record<EnquiryOutcomeKey, 'info' | 'success' | 'neutral'> = { OPEN: 'info', WON: 'success', LOST: 'neutral' };
